import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import classNames from 'classnames';
import React from 'react';
import { soulWiseTheme } from '../../../../mui-theme';
import { ReactComponent as WarningIcon } from './_icons/Warning.svg';
import css from './InlineAlert.module.css';

const ALERT_SEVERITY = {
  WARNING: 'warning',
  INFO: 'info',
  ERROR: 'error',
  TIME: 'time',
  BLANK: 'blank',
  GREEN: 'green',
};

const SEVERITY_CONFIG = {
  [ALERT_SEVERITY.BLANK]: {
    icon: null,
    color: soulWiseTheme.palette.info.main,
  },
  [ALERT_SEVERITY.INFO]: {
    icon: InfoIcon,
    color: soulWiseTheme.palette.info.main,
  },
  [ALERT_SEVERITY.WARNING]: {
    icon: WarningIcon,
    color: soulWiseTheme.palette.warning.main,
  },
  [ALERT_SEVERITY.ERROR]: {
    icon: ErrorIcon,
    color: soulWiseTheme.palette.error.main,
  },
  [ALERT_SEVERITY.TIME]: {
    icon: AccessTimeFilledIcon,
    color: soulWiseTheme.palette.white80,
    bgColor: soulWiseTheme.palette.white10,
  },
  [ALERT_SEVERITY.GREEN]: {
    color: soulWiseTheme.palette.white80,
    bgColor: soulWiseTheme.palette.greenLight15,
  },
};

const InlineAlert = ({ rootClassName, title, children, severity = ALERT_SEVERITY.INFO, icon }) => {
  const rootClasses = classNames(css.root, rootClassName);
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG[ALERT_SEVERITY.INFO];

  const renderIcon = () => {
    // Explicitly hide icon when null/false is passed
    if (icon === null || icon === false) return null;

    let node = null;
    const isCustomIcon = icon !== undefined;

    if (typeof icon === 'string' || typeof icon === 'number') {
      // Emoji or textual icon
      node = <span>{icon}</span>;
    } else if (React.isValidElement(icon)) {
      // Already a rendered React element (e.g. <InfoIcon />)
      // Respect the size that was set on the custom icon
      node = icon;
    } else if (icon) {
      // A React component type was provided (e.g. InfoIcon)
      // Respect the size that was set on the custom icon
      const ProvidedIcon = icon;
      node = <ProvidedIcon />;
    } else if (config.icon) {
      // Fallback to severity-based default icon - set to 18px
      const DefaultIcon = config.icon;
      node = <DefaultIcon sx={{ fontSize: '18px' }} />;
    }

    return node ? (
      <div className={css.icon} style={{ color: config.color }}>
        {node}
      </div>
    ) : null;
  };

  return (
    <div className={rootClasses} style={{ backgroundColor: config.bgColor }}>
      {renderIcon()}
      <div className={css.container}>
        {title ? <div className={css.title}>{title}</div> : null}
        <div className={css.text}>{children}</div>
      </div>
    </div>
  );
};

export { ALERT_SEVERITY, InlineAlert };
